const chapter = {
  id: 'sets-relations-functions',
  slug: 'sets-relations-functions',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Main + Advanced',
  title: {
    english: 'Sets, Relations & Functions',
        hindi: 'समुच्चय, सम्बन्ध और फलन',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Easy-Medium',
        hindi: 'आसान-मध्यम',
  },
  weightage: {
    english: '1–2 questions in JEE Main',
        hindi: 'जेईई मेन में 1-2 प्रश्न',
  },
  syllabusTags: ['Algebra', 'JEE-Main', 'JEE-Advanced', 'Class-11'],
  sections: [
    {
      id: 'sets-intro',
      title: {
        english: 'Sets — Definition & Notation',
        hindi: 'समुच्चय — परिभाषा और संकेत',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Set',
        hindi: 'सेट करें',
          },
          body: {
            english: 'A **set** is a well-defined collection of distinct objects called **elements**. Membership is denoted $x \\in A$; non-membership $x \\notin A$. Sets are usually denoted with capital letters.',
        hindi: 'एक **सेट** अलग-अलग वस्तुओं का एक अच्छी तरह से परिभाषित संग्रह है जिसे **तत्व** कहा जाता है। सदस्यता को $x \\in A$ दर्शाया गया है; गैर-सदस्यता $x \\notin A$. सेट को आमतौर पर बड़े अक्षरों से दर्शाया जाता है। **रोस्टर फॉर्म**: $x \\in A$',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Roster form**: $A = \\{1, 2, 3, 4\\}$',
        hindi: '**रोस्टर फॉर्म**: $A = \\{1, 2, 3, 4\\}$',
            },
            {
              english: '**Set-builder form**: $A = \\{x \\in \\mathbb{N} : x \\le 4\\}$',
        hindi: '**सेट-बिल्डर फॉर्म**: $A = \\{x \\in \\mathbb{N}: x \\le 4\\}$',
            },
            {
              english: '**Empty set** ($\\emptyset$ or $\\{\\}$): contains no elements; $|\\emptyset| = 0$.',
        hindi: '**खाली सेट** ($\\emptyset$ या $\\{\\}$): इसमें कोई तत्व नहीं है; $|\\emptyset| = 0$.',
            },
            {
              english: '**Singleton**: a set with exactly one element.',
        hindi: '**सिंगलटन**: बिल्कुल एक तत्व वाला एक सेट।',
            },
            {
              english: '**Finite vs infinite**, **equal** ($A = B$ iff same elements), **equivalent** ($|A| = |B|$).',
        hindi: '**परिमित बनाम अनंत**, **बराबर** ($A = B$ यदि समान तत्व), **समतुल्य** ($|A| = |B|$)।',
            },
          ],
        },
        {
          type: 'definition',
          term: {
            english: 'Subset',
        hindi: 'उपसमुच्चय',
          },
          body: {
            english: '$A \\subseteq B$ iff every element of $A$ is also in $B$. **Proper subset**: $A \\subsetneq B$ if $A \\subseteq B$ and $A \\ne B$.',
        hindi: '$A \\subseteq B$ यदि $A$ का प्रत्येक तत्व $B$ में भी है। **उचित उपसमुच्चय**: $A \\subsetneq B$ यदि $A \\subseteq B$ और $A \\ne B$।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Power Set',
        hindi: 'सत्ता स्थापित',
          },
          statement: {
            english: 'For a set $A$ with $|A| = n$, the power set $\\mathcal{P}(A)$ has $2^n$ elements.',
        hindi: '$|A| = n$ के साथ $A$ सेट के लिए, पावर सेट $\\mathcal{P}(A)$ में $2^n$ तत्व हैं।',
          },
        },
      ],
    },
    {
      id: 'set-ops',
      title: {
        english: 'Set Operations & Identities',
        hindi: 'समुच्चय संक्रियाएँ और सर्वसमिकाएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Union**: $A \\cup B = \\{x : x \\in A \\text{ or } x \\in B\\}$',
        hindi: '**संघ**: $A \\cup B = \\{x: x \\in A \\text{ or } x \\in B\\}$',
            },
            {
              english: '**Intersection**: $A \\cap B = \\{x : x \\in A \\text{ and } x \\in B\\}$',
        hindi: '**इंटरसेक्शन**: $A \\cap B = \\{x: x \\in A \\text{ and } x \\in B\\}$',
            },
            {
              english: '**Difference**: $A - B = \\{x : x \\in A,\\ x \\notin B\\}$',
        hindi: '**अंतर**: $A - B = \\{x: x \\in A,\\ x \\notin B\\}$',
            },
            {
              english: '**Complement**: $A^c = U - A$ (relative to universal set $U$)',
        hindi: '**पूरक**: $A^c = U - A$ (यूनिवर्सल सेट $U$ के सापेक्ष)',
            },
            {
              english: '**Symmetric difference**: $A \\triangle B = (A - B) \\cup (B - A) = (A \\cup B) - (A \\cap B)$',
        hindi: '**सममित अंतर**: $A \\triangle B = (A - B) \\cup (B - A) = (A \\cup B) - (A \\cap B)$',
            },
          ],
        },
        {
          type: 'figure',
          svg: `
<svg viewBox="0 0 360 220" xmlns="http://www.w3.org/2000/svg">
  <rect x="10" y="10" width="340" height="200" fill="#f9fafb" stroke="#374151"/>
  <text x="20" y="28" font-size="12" fill="#374151">U</text>
  <circle cx="140" cy="115" r="68" fill="#dbeafe" fill-opacity="0.7" stroke="#3b82f6" stroke-width="2"/>
  <circle cx="220" cy="115" r="68" fill="#fee2e2" fill-opacity="0.7" stroke="#ef4444" stroke-width="2"/>
  <text x="92" y="60" font-size="14" fill="#1e40af" font-weight="700">A</text>
  <text x="262" y="60" font-size="14" fill="#991b1b" font-weight="700">B</text>
  <text x="100" y="120" font-size="11" fill="#1e3a8a" text-anchor="middle">A − B</text>
  <text x="180" y="120" font-size="11" fill="#374151" text-anchor="middle">A ∩ B</text>
  <text x="260" y="120" font-size="11" fill="#7f1d1d" text-anchor="middle">B − A</text>
  <text x="180" y="200" font-size="11" fill="#6b7280" text-anchor="middle">|A ∪ B| = |A| + |B| − |A ∩ B|</text>
</svg>`,
          caption: {
            english: 'Two-set Venn diagram with three regions: A−B, A∩B, B−A. Inclusion-exclusion: |A∪B| = |A| + |B| − |A∩B|.',
        hindi: 'तीन क्षेत्रों के साथ दो-सेट वेन आरेख: A−B, A∩B, B−A। समावेशन-बहिष्करण: |A∪B| = |ए| + |बी| − |A∩B|',
          },
        },
        {
          type: 'table',
          headers: ['Identity', 'Statement'],
          rows: [
            ['Commutative', '$A \\cup B = B \\cup A,\\ A \\cap B = B \\cap A$'],
            ['Associative', '$(A \\cup B) \\cup C = A \\cup (B \\cup C)$'],
            ['Distributive', '$A \\cap (B \\cup C) = (A \\cap B) \\cup (A \\cap C)$'],
            ['De Morgan', '$(A \\cup B)^c = A^c \\cap B^c,\\ (A \\cap B)^c = A^c \\cup B^c$'],
            ['Identity', '$A \\cup \\emptyset = A,\\ A \\cap U = A$'],
            ['Idempotent', '$A \\cup A = A,\\ A \\cap A = A$'],
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Inclusion-Exclusion (3 sets)',
        hindi: 'समावेशन-बहिष्करण (3 सेट)',
          },
          statement: {
            english: '$|A \\cup B \\cup C| = |A| + |B| + |C| - |A \\cap B| - |B \\cap C| - |C \\cap A| + |A \\cap B \\cap C|$.',
        hindi: '$|ए \\cap बी \\cap सी| = |ए| + |बी| + |सी| - |ए कैप बी| - |बी \\cap सी| - |सी कैप ए| + |ए \\cap बी \\cap सी|$.',
          },
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'In a class of 60, 35 like cricket, 30 like football, and 10 like both. How many like neither?',
        hindi: '60 की कक्षा में, 35 को क्रिकेट पसंद है, 30 को फुटबॉल पसंद है, और 10 को दोनों पसंद हैं। कितने लोगों को कोई पसंद नहीं है?',
          },
          solution: {
            english: '$|C \\cup F| = 35 + 30 - 10 = 55$. Neither $= 60 - 55 = 5$.',
        hindi: '$|सी \\cap एफ| = 35 + 30 - 10 = 55$. न तो $= 60 - 55 = 5$।',
          },
        },
      ],
    },
    {
      id: 'cartesian-relations',
      title: {
        english: 'Cartesian Product & Relations',
        hindi: 'कार्तीय गुणनफल और सम्बन्ध',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Cartesian product',
        hindi: 'कार्टेशियन उत्पाद',
          },
          body: {
            english: '$A \\times B = \\{(a, b) : a \\in A,\\ b \\in B\\}$. If $|A| = m, |B| = n$, then $|A \\times B| = mn$.',
        hindi: '$A \\times B = \\{(a, b): a \\in A,\\ b \\in B\\}$. यदि $|A| = m, |B| = n$, तो $|A \\times B| = mn$।',
          },
        },
        {
          type: 'definition',
          term: {
            english: 'Relation',
        hindi: 'संबंध',
          },
          body: {
            english: 'A **relation** $R$ from $A$ to $B$ is any subset of $A \\times B$. We write $aRb$ if $(a,b) \\in R$.',
        hindi: '$A$ से $B$ तक एक **संबंध** $R$, $A \\times B$ का कोई उपसमुच्चय है। यदि $(a,b) \\in R$ है तो हम $aRb$ लिखते हैं।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Domain** of $R$: set of first coordinates.',
        hindi: '$R$ का **डोमेन**: पहले निर्देशांक का सेट।',
            },
            {
              english: '**Range** of $R$: set of second coordinates.',
        hindi: '$R$ की **रेंज**: दूसरे निर्देशांक का सेट।',
            },
            {
              english: '**Codomain**: $B$ itself (range $\\subseteq$ codomain).',
        hindi: '**कोडोमेन**: $B$ स्वयं (रेंज $\\subseteq$ कोडोमेन)।',
            },
            {
              english: 'A relation on $A$ means $R \\subseteq A \\times A$.',
        hindi: '$A$ पर एक संबंध का अर्थ $R \\subseteq A \\times A$ है।',
            },
            {
              english: 'Total number of relations from $A$ to $B$: $2^{mn}$.',
        hindi: '$A$ से $B$ तक संबंधों की कुल संख्या: $2^{mn}$।',
            },
          ],
        },
        {
          type: 'definition',
          term: {
            english: 'Types of relations on a set $A$',
        hindi: 'सेट पर संबंधों के प्रकार $A$',
          },
          body: {
            english: `• **Reflexive**: $aRa$ for all $a \\in A$.
• **Symmetric**: $aRb \\Rightarrow bRa$.
• **Transitive**: $aRb$ and $bRc$ $\\Rightarrow aRc$.
• **Equivalence**: reflexive + symmetric + transitive.
• **Antisymmetric**: $aRb$ and $bRa \\Rightarrow a = b$.`,
            hindi: '• **रिफ्लेक्टिव**: $aRa$ सभी $a \\in A$ के लिए। • **सममित**: $aRb \\Rightarrow bRa$. • **सकर्मक**: $aRb$ और $bRc$ $\\Rightarrow aRc$। • **समतुल्यता**: प्रतिवर्ती + सममित + सकर्मक। • **एंटीसिमेट्रिक**: $aRb$ और $bRa \\Rightarrow a = b$।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Equivalence classes',
        hindi: 'समतुल्य वर्ग',
          },
          statement: {
            english: 'An equivalence relation on $A$ partitions $A$ into disjoint **equivalence classes** whose union is $A$.',
        hindi: '$A$ विभाजन $A$ पर एक समतुल्य संबंध असंयुक्त **समतुल्य वर्गों** में है जिसका मिलन $A$ है।',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Show that "is parallel to" on the set of straight lines is an equivalence relation.',
        hindi: 'दिखाएँ कि सीधी रेखाओं के सेट पर "समानांतर है" एक तुल्यता संबंध है।',
          },
          solution: {
            english: 'Reflexive: every line is parallel to itself. Symmetric: $\\ell_1 \\parallel \\ell_2 \\Rightarrow \\ell_2 \\parallel \\ell_1$. Transitive: $\\ell_1 \\parallel \\ell_2,\\ \\ell_2 \\parallel \\ell_3 \\Rightarrow \\ell_1 \\parallel \\ell_3$. ✓',
        hindi: 'रिफ्लेक्सिव: प्रत्येक रेखा स्वयं के समानांतर होती है। सममित: $\\ell_1 \\parallel \\ell_2 \\Rightarrow \\ell_2 \\parallel \\ell_1$। सकर्मक: $\\ell_1 \\parallel \\ell_2,\\ \\ell_2 \\parallel \\ell_3 \\Rightarrow \\ell_1 \\parallel \\ell_3$। ✓',
          },
        },
      ],
    },
    {
      id: 'functions-intro',
      title: {
        english: 'Functions — Definition & Notation',
        hindi: 'फलन — परिभाषा और संकेत',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Function',
        hindi: 'फ़ंक्शन',
          },
          body: {
            english: 'A **function** $f : A \\to B$ assigns to each element $a \\in A$ exactly one element $f(a) \\in B$. Here $A$ is the **domain**, $B$ the **codomain**, and $\\{f(a) : a \\in A\\}$ the **range** ($\\subseteq B$).',
        hindi: 'एक **फ़ंक्शन** $f: A \\to B$ प्रत्येक तत्व $a \\in A$ को बिल्कुल एक तत्व $f(a) \\in B$ निर्दिष्ट करता है। यहां $A$ **डोमेन** है, $B$ **कोडोमेन** है, और $\\{f(a): a \\in A\\}$ **रेंज** ($\\subseteq B$) है।',
          },
        },
        {
          type: 'figure',
          svg: `
<svg viewBox="0 0 420 220" xmlns="http://www.w3.org/2000/svg">
  <ellipse cx="90" cy="110" rx="55" ry="90" fill="none" stroke="#3b82f6" stroke-width="2"/>
  <ellipse cx="330" cy="110" rx="55" ry="90" fill="none" stroke="#ef4444" stroke-width="2"/>
  <text x="25" y="25" font-size="13" fill="#1e40af" font-weight="700">A (Domain)</text>
  <text x="280" y="25" font-size="13" fill="#991b1b" font-weight="700">B (Codomain)</text>
  <circle cx="90" cy="60" r="4" fill="#1f2937"/><text x="68" y="64" font-size="12" fill="#1f2937">a</text><circle cx="90" cy="110" r="4" fill="#1f2937"/><text x="68" y="114" font-size="12" fill="#1f2937">b</text><circle cx="90" cy="160" r="4" fill="#1f2937"/><text x="68" y="164" font-size="12" fill="#1f2937">c</text>
  <circle cx="330" cy="50" r="4" fill="#1f2937"/><text x="342" y="54" font-size="12" fill="#1f2937">w</text><circle cx="330" cy="95" r="4" fill="#1f2937"/><text x="342" y="99" font-size="12" fill="#1f2937">x</text><circle cx="330" cy="140" r="4" fill="#1f2937"/><text x="342" y="144" font-size="12" fill="#1f2937">y</text><circle cx="330" cy="185" r="4" fill="#1f2937"/><text x="342" y="189" font-size="12" fill="#1f2937">z</text>
  <line x1="94" y1="60" x2="326" y2="140" stroke="#10b981" stroke-width="1.8" marker-end="url(#fmar)"/>
  <line x1="94" y1="110" x2="326" y2="140" stroke="#10b981" stroke-width="1.8" marker-end="url(#fmar)"/>
  <line x1="94" y1="160" x2="326" y2="185" stroke="#10b981" stroke-width="1.8" marker-end="url(#fmar)"/>
  <defs>
    <marker id="fmar" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
      <path d="M0,0 L10,5 L0,10 z" fill="#10b981"/>
    </marker>
  </defs>
  <text x="210" y="210" font-size="11" fill="#6b7280" text-anchor="middle">Each element of A has exactly one image in B → this is a function.</text>
</svg>`,
          caption: {
            english: 'A function: every element of A maps to exactly one element of B (multiple A-elements may share the same image).',
        hindi: 'एक फ़ंक्शन: A का प्रत्येक तत्व B के ठीक एक तत्व को मैप करता है (एकाधिक A-तत्व एक ही छवि साझा कर सकते हैं)। **ऊर्ध्वाधर रेखा परीक्षण**: -प्लेन में एक वक्र के फ़ंक्शन का ग्राफ़ है यदि प्रत्येक लंबवत रेखा अधिकतम एक बिंदु पर वक्र से मिलती है।',
          },
        },
        {
          type: 'note',
          text: {
            english: '**Vertical line test**: a curve in the $xy$-plane is the graph of a function of $x$ iff every vertical line meets the curve in at most one point.',
        hindi: '**ऊर्ध्वाधर रेखा परीक्षण**: $xy$-प्लेन में एक वक्र $x$ के फ़ंक्शन का ग्राफ़ है यदि प्रत्येक लंबवत रेखा अधिकतम एक बिंदु पर वक्र से मिलती है।',
          },
        },
      ],
    },
    {
      id: 'function-types',
      title: {
        english: 'Types of Functions (One-One, Onto, Bijective)',
        hindi: 'फलनों के प्रकार (एकैकी, आच्छादक, द्विअर्थी)',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Injective (One-One)',
        hindi: 'इंजेक्शन (एक-एक)',
          },
          body: {
            english: '$f$ is one-one iff $f(x_1) = f(x_2) \\Rightarrow x_1 = x_2$. Equivalently, distinct inputs give distinct outputs.',
        hindi: '$f$ एक-एक है यदि $f(x_1) = f(x_2) \\Rightarrow x_1 = x_2$। समान रूप से, अलग-अलग इनपुट अलग-अलग आउटपुट देते हैं।',
          },
        },
        {
          type: 'definition',
          term: {
            english: 'Surjective (Onto)',
        hindi: 'विशेषण (ऑनटू)',
          },
          body: {
            english: '$f$ is onto iff for every $y \\in B$ there exists $x \\in A$ with $f(x) = y$, i.e. range $= B$.',
        hindi: '$f$ प्रत्येक $y \\in B$ के लिए IF पर है, $f(x) = y$ के साथ $x \\in A$ मौजूद है, यानी रेंज $= B$।',
          },
        },
        {
          type: 'definition',
          term: {
            english: 'Bijective',
        hindi: 'विशेषण',
          },
          body: {
            english: '$f$ is bijective iff it is both one-one and onto. Bijections have inverses.',
        hindi: '$f$ विशेषण है यदि यह एक-एक और आच्छादक दोनों है। आक्षेपों में व्युत्क्रम होते हैं। **एक-एक के लिए परीक्षण**: बीजगणितीय रूप से $f$ मान लें और समाप्त करें। या **एकरसता** ( सख्ती से एक-एक) का उपयोग करें। **क्षैतिज रेखा परीक्षण** ग्राफिक रूप से भी काम करता है।',
          },
        },
        {
          type: 'tip',
          text: {
            english: '**Test for one-one**: assume $f(x_1) = f(x_2)$ algebraically and conclude $x_1 = x_2$. Or use **monotonicity** ($f$ strictly increasing/decreasing $\\Rightarrow$ one-one). **Horizontal line test** also works graphically.',
        hindi: '**एक-एक के लिए परीक्षण**: बीजगणितीय रूप से $f(x_1) = f(x_2)$ मान लें और $x_1 = x_2$ समाप्त करें। या **एकरसता** ($f$ सख्ती से increasing/decreasing $\\Rightarrow$ एक-एक) का उपयोग करें। **क्षैतिज रेखा परीक्षण** ग्राफिक रूप से भी काम करता है।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Number of functions',
        hindi: 'कार्यों की संख्या',
          },
          statement: {
            english: `For finite sets $|A| = m, |B| = n$:
• Total functions: $n^m$.
• Injective ($m \\le n$): $^nP_m = \\dfrac{n!}{(n-m)!}$.
• Surjective: $\\sum_{r=0}^{n} (-1)^r \\binom{n}{r}(n-r)^m$.
• Bijective ($m = n$): $n!$.`,
            hindi: 'परिमित सेटों के लिए $|A| = m, |B| = n$: • कुल कार्य: $n^m$. • इंजेक्शन ($m \\le n$): $^nP_m = \\dfrac{n!}{(n-m)!}$। • विशेषण: $\\sum_{r=0}^{n} (-1)^r \\binom{n}{r}(n-r)^m$. • विशेषण ($m = n$): $n!$।',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'Check whether $f : \\mathbb{R} \\to \\mathbb{R},\\ f(x) = x^3 + 1$ is bijective.',
        hindi: 'जांचें कि क्या $f: \\mathbb{R} \\to \\mathbb{R},\\ f(x) = x^3 + 1$ विशेषण है।',
          },
          solution: {
            english: 'Strictly increasing (since $f\'(x) = 3x^2 \\ge 0$, and $= 0$ only at one point) ⇒ one-one. As $x \\to \\pm\\infty,\\ f(x) \\to \\pm\\infty$ ⇒ onto $\\mathbb{R}$. Bijective ✓.',
        hindi: 'सख्ती से बढ़ रहा है ($f\\\'(x) = 3x^2 \\ge 0$ के बाद से, और $= 0$ केवल एक बिंदु पर) ⇒ एक-एक। जैसे $x \\to \\pm\\infty,\\\\ f(x) \\to \\pm\\infty$ ⇒ $\\mathbb{R}$ पर। विशेषण ✓.',
          },
        },
      ],
    },
    {
      id: 'composition-inverse',
      title: {
        english: 'Composition & Inverse Functions',
        hindi: 'संयोजन और प्रतिलोम फलन',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Composition',
        hindi: 'रचना',
          },
          body: {
            english: 'For $f : A \\to B$ and $g : B \\to C$, the **composition** $g \\circ f : A \\to C$ is defined by $(g \\circ f)(x) = g(f(x))$. In general $g \\circ f \\ne f \\circ g$.',
        hindi: '$f: A \\to B$ और $g: B \\to C$ के लिए, **संरचना** $g \\circ f: A \\to C$ को $(g \\circ f)(x) = g(f(x))$ द्वारा परिभाषित किया गया है। सामान्य तौर पर $g \\circ f \\ne f \\circ g$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Composition is **associative**: $h \\circ (g \\circ f) = (h \\circ g) \\circ f$.',
        hindi: 'रचना **साहचर्य** है: $h \\circ (g \\circ f) = (h \\circ g) \\circ f$.',
            },
            {
              english: 'If $f, g$ are both one-one (resp. onto, bijective), then so is $g \\circ f$.',
        hindi: 'यदि $f, g$ दोनों एक-एक (सम्मानित पर, विशेषण) हैं, तो $g \\circ f$ भी एक-एक हैं।',
            },
          ],
        },
        {
          type: 'definition',
          term: {
            english: 'Inverse',
        hindi: 'उलटा',
          },
          body: {
            english: 'A function $f : A \\to B$ is **invertible** iff it is bijective. The inverse $f^{-1} : B \\to A$ satisfies $f^{-1}(f(x)) = x$ and $f(f^{-1}(y)) = y$.',
        hindi: 'एक फलन $f: A \\to B$ **उलटा** है यदि यह विशेषणात्मक है। व्युत्क्रम $f^{-1}: B \\to A$ $f^{-1}(f(x)) = x$ और $f(f^{-1}(y)) = y$ को संतुष्ट करता है।',
          },
        },
        {
          type: 'tip',
          text: {
            english: '**Finding $f^{-1}$**: write $y = f(x)$, swap $x$ and $y$, solve for $y$. Graph of $f^{-1}$ is the reflection of graph of $f$ in the line $y = x$.',
        hindi: '**$f^{-1}$** ढूँढना: $y = f(x)$ लिखें, $x$ और $y$ को स्वैप करें, $y$ को हल करें। $f^{-1}$ का ग्राफ़ $y = x$ पंक्ति में $f$ के ग्राफ़ का प्रतिबिंब है।',
          },
        },
        {
          type: 'equation',
          latex: '(g \\circ f)^{-1} = f^{-1} \\circ g^{-1}',
        },
        {
          type: 'example',
          number: 4,
          problem: {
            english: 'Find the inverse of $f(x) = \\dfrac{2x + 3}{x - 1},\\ x \\ne 1$.',
        hindi: '$f(x) = \\dfrac{2x + 3}{x - 1},\\ x \\ne 1$ का व्युत्क्रम ज्ञात कीजिए।',
          },
          solution: {
            english: 'Let $y = \\dfrac{2x+3}{x-1}$. Cross-multiply: $y(x-1) = 2x + 3 \\Rightarrow x(y - 2) = y + 3 \\Rightarrow x = \\dfrac{y+3}{y-2}$. So $f^{-1}(x) = \\dfrac{x+3}{x-2}$.',
        hindi: 'चलो $y = \\dfrac{2x+3}{x-1}$. क्रॉस-गुणा: $y(x-1) = 2x + 3 \\Rightarrow x(y - 2) = y + 3 \\Rightarrow x = \\dfrac{y+3}{y-2}$. तो $f^{-1}(x) = \\dfrac{x+3}{x-2}$.',
          },
        },
      ],
    },
    {
      id: 'standard-functions',
      title: {
        english: 'Standard Functions, Domain & Range',
        hindi: 'मानक फलन, प्रान्त और परिसर',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Function', 'Domain', 'Range'],
          rows: [
            ['$f(x) = x$ (identity)', '$\\mathbb{R}$', '$\\mathbb{R}$'],
            ['$f(x) = c$ (constant)', '$\\mathbb{R}$', '$\\{c\\}$'],
            ['$f(x) = |x|$', '$\\mathbb{R}$', '$[0, \\infty)$'],
            ['$f(x) = [x]$ (greatest integer)', '$\\mathbb{R}$', '$\\mathbb{Z}$'],
            ['$f(x) = \\{x\\}$ (fractional part)', '$\\mathbb{R}$', '$[0, 1)$'],
            ['$f(x) = \\sgn(x)$', '$\\mathbb{R}$', '$\\{-1, 0, 1\\}$'],
            ['$f(x) = 1/x$', '$\\mathbb{R}\\setminus\\{0\\}$', '$\\mathbb{R}\\setminus\\{0\\}$'],
            ['$f(x) = \\sqrt x$', '$[0, \\infty)$', '$[0, \\infty)$'],
            ['$f(x) = e^x$', '$\\mathbb{R}$', '$(0, \\infty)$'],
            ['$f(x) = \\ln x$', '$(0, \\infty)$', '$\\mathbb{R}$'],
            ['$f(x) = \\sin x$', '$\\mathbb{R}$', '$[-1, 1]$'],
            ['$f(x) = \\tan x$', '$\\mathbb{R}\\setminus\\{(2k+1)\\pi/2\\}$', '$\\mathbb{R}$'],
          ],
        },
        {
          type: 'tip',
          text: {
            english: '**Domain rules**: denominators $\\ne 0$, even-roots need $\\ge 0$ argument, $\\log$ needs $> 0$ argument, $\\arcsin/\\arccos$ need argument in $[-1, 1]$.',
        hindi: '**डोमेन नियम**: हर $\\ne 0$, सम-मूलों को $\\ge 0$ तर्क की आवश्यकता है, $\\log$ को $> 0$ तर्क की आवश्यकता है, $\\arcsin/\\arccos$ को $[-1, 1]$ में तर्क की आवश्यकता है।',
          },
        },
        {
          type: 'example',
          number: 5,
          problem: {
            english: 'Find the domain of $f(x) = \\sqrt{4 - x^2} + \\dfrac{1}{\\ln(x - 1)}$.',
        hindi: '$f(x) = \\sqrt{4 - x^2} + \\dfrac{1}{\\ln(x - 1)}$ का डोमेन खोजें।',
          },
          solution: {
            english: '$4 - x^2 \\ge 0 \\Rightarrow x \\in [-2, 2]$. $x - 1 > 0$ and $\\ln(x-1) \\ne 0 \\Rightarrow x > 1,\\ x \\ne 2$. Intersection: $x \\in (1, 2)$.',
        hindi: '$4 - x^2 \\ge 0 \\Rightarrow x \\in [-2, 2]$। $x - 1 > 0$ और $\\ln(x-1) \\ne 0 \\Rightarrow x > 1,\\ x \\ne 2$। प्रतिच्छेदन: $x \\in (1, 2)$।',
          },
        },
      ],
    },
    {
      id: 'even-odd-periodic',
      title: {
        english: 'Even, Odd & Periodic Functions',
        hindi: 'सम, विषम और आवर्ती फलन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Even**: $f(-x) = f(x)$ — graph symmetric about $y$-axis (e.g. $\\cos x,\\ x^2$).',
        hindi: '**सम**: $f(-x) = f(x)$ — $y$-अक्ष के बारे में ग्राफ़ सममित (उदा. $\\cos x,\\\\ x^2$)।',
            },
            {
              english: '**Odd**: $f(-x) = -f(x)$ — graph symmetric about origin (e.g. $\\sin x,\\ x^3$).',
        hindi: '**अजीब**: $f(-x) = -f(x)$ - मूल के बारे में ग्राफ सममित (जैसे $\\sin x,\\\\ x^3$)।',
            },
            {
              english: 'Any function $f$ can be uniquely written as $f = E + O$ where $E(x) = \\dfrac{f(x) + f(-x)}{2}$ (even) and $O(x) = \\dfrac{f(x) - f(-x)}{2}$ (odd).',
        hindi: 'किसी भी फ़ंक्शन $f$ को विशिष्ट रूप से $f = E + O$ के रूप में लिखा जा सकता है जहां $E(x) = \\dfrac{f(x) + f(-x)}{2}$ (सम) और $O(x) = \\dfrac{f(x) - f(-x)}{2}$ (विषम) होता है। **आवधिक**: $f$ अवधि $f = E + O$ के साथ आवधिक है यदि सभी $O(x) = \\dfrac{f(x) - f(-x)}{2}$ के लिए $E(x) = \\dfrac{f(x) + f(-x)}{2}$ है। ऐसा सबसे छोटा सकारात्मक **मौलिक अवधि** है।',
            },
            {
              english: '**Periodic**: $f$ is periodic with period $T$ if $f(x + T) = f(x)$ for all $x$. Smallest such positive $T$ is the **fundamental period**.',
        hindi: '**आवधिक**: $f$ अवधि $T$ के साथ आवधिक है यदि सभी $x$ के लिए $f(x + T) = f(x)$ है। ऐसा सबसे छोटा सकारात्मक $T$ **मौलिक अवधि** है।',
            },
          ],
        },
        {
          type: 'example',
          number: 6,
          problem: {
            english: 'Find the period of $f(x) = \\sin 2x + \\cos 3x$.',
        hindi: '$f(x) = \\sin 2x + \\cos 3x$ की अवधि ज्ञात कीजिए।',
          },
          solution: {
            english: 'Period of $\\sin 2x = \\pi$; period of $\\cos 3x = 2\\pi/3$. Combined period $= \\operatorname{lcm}(\\pi, 2\\pi/3) = 2\\pi$.',
        hindi: '$\\sin 2x = \\pi$ की अवधि; $\\cos 3x = 2\\pi/3$ की अवधि. संयुक्त अवधि $= \\operatorname{lcm}(\\pi, 2\\pi/3) = 2\\pi$.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '|\\mathcal{P}(A)| = 2^{|A|}',
      desc: {
        english: 'Power set size',
        hindi: 'पावर सेट का आकार',
      },
    },
    {
      latex: '|A \\cup B| = |A| + |B| - |A \\cap B|',
      desc: {
        english: '2-set inclusion-exclusion',
        hindi: '2-सेट समावेशन-बहिष्करण',
      },
    },
    {
      latex: '(A \\cup B)^c = A^c \\cap B^c',
      desc: {
        english: 'De Morgan\'s law',
        hindi: 'डी मॉर्गन का नियम',
      },
    },
    {
      latex: '|A \\times B| = |A|\\,|B|',
      desc: {
        english: 'Cartesian product size',
        hindi: 'कार्टेशियन उत्पाद का आकार',
      },
    },
    {
      latex: 'g \\circ f(x) = g(f(x))',
      desc: {
        english: 'Composition',
        hindi: 'रचना',
      },
    },
    {
      latex: '(g \\circ f)^{-1} = f^{-1} \\circ g^{-1}',
      desc: {
        english: 'Inverse of composition',
        hindi: 'रचना का व्युत्क्रम',
      },
    },
    {
      latex: '\\text{Bijections from } A \\text{ to } A : n!',
      desc: {
        english: 'Counting bijections',
        hindi: 'अनुमानों की गिनती',
      },
    },
    {
      latex: 'f = \\tfrac{f(x)+f(-x)}{2} + \\tfrac{f(x)-f(-x)}{2}',
      desc: {
        english: 'Even + odd decomposition',
        hindi: 'सम + विषम अपघटन',
      },
    },
  ],
  pyqs: [
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'Sets',
      problem: {
        english: 'If $A = \\{x \\in \\mathbb{R} : x^2 - 5x + 6 = 0\\}$ and $B = \\{2, 3, 5\\}$, find $A \\cap B$.',
        hindi: 'यदि $A = \\{x \\in \\mathbb{R}: x^2 - 5x + 6 = 0\\}$ और $B = \\{2, 3, 5\\}$, तो $A \\cap B$ खोजें।',
      },
      answer: {
        english: '$\\{2, 3\\}$',
        hindi: '$\\{2, 3\\}$',
      },
      solution: {
        english: '$A = \\{2, 3\\}$, intersection with $B = \\{2, 3\\}$.',
        hindi: '$A = \\{2, 3\\}$, $B के साथ प्रतिच्छेदन = \\{2, 3\\}$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Functions',
      problem: {
        english: 'Domain of $f(x) = \\sqrt{\\log_{1/2}(x^2 - x + 1)}$ is:',
        hindi: '$f(x) = \\sqrt{\\log_{1/2}(x^2 - x + 1)}$ का डोमेन है:',
      },
      answer: {
        english: '$[0, 1]$',
        hindi: '$[0, 1]$',
      },
      solution: {
        english: 'Need $\\log_{1/2}(x^2 - x + 1) \\ge 0 \\Rightarrow x^2 - x + 1 \\le 1 \\Rightarrow x^2 - x \\le 0 \\Rightarrow x \\in [0, 1]$.',
        hindi: '$\\log_{1/2}(x^2 - x + 1) \\ge 0 \\Rightarrow x^2 - x + 1 \\le 1 \\Rightarrow x^2 - x \\le 0 \\Rightarrow x \\in [0, 1]$ की आवश्यकता है।',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Relations',
      problem: {
        english: 'Number of reflexive relations on a set with 3 elements is:',
        hindi: '3 तत्वों वाले सेट पर रिफ्लेक्सिव संबंधों की संख्या है:',
      },
      answer: {
        english: '$2^{6} = 64$',
        hindi: '$2^{6} = 64$',
      },
      solution: {
        english: 'Reflexive needs all 3 diagonal pairs. Other $9 - 3 = 6$ pairs are free: $2^6 = 64$.',
        hindi: 'रिफ्लेक्सिव को सभी 3 विकर्ण जोड़े की आवश्यकता होती है। अन्य $9 - 3 = 6$ जोड़े निःशुल्क हैं: $2^6 = 64$।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Functions',
      problem: {
        english: 'If $f(x) = \\dfrac{x}{x - 1}$, find $f(f(x))$.',
        hindi: 'यदि $f(x) = \\dfrac{x}{x - 1}$, तो $f(f(x))$ खोजें।',
      },
      answer: {
        english: '$x$',
        hindi: '$x$',
      },
      solution: {
        english: '$f(f(x)) = \\dfrac{x/(x-1)}{x/(x-1) - 1} = \\dfrac{x/(x-1)}{1/(x-1)} = x$. So $f$ is its own inverse.',
        hindi: '$f(f(x)) = \\dfrac{x/(x-1)}{x/(x-1) - 1} = \\dfrac{x/(x-1)}{1/(x-1)} = x$. तो $f$ स्वयं का व्युत्क्रम है।',
      },
    },
    {
      year: 2019,
      exam: 'JEE Advanced',
      tag: 'Counting',
      problem: {
        english: 'Number of onto functions from a set of 5 elements to a set of 3 elements is:',
        hindi: '5 तत्वों के सेट से 3 तत्वों के सेट तक ऑन्टो फ़ंक्शंस की संख्या है:',
      },
      answer: {
        english: '$150$',
        hindi: '$150$',
      },
      solution: {
        english: 'Use formula $\\sum (-1)^r \\binom{3}{r}(3-r)^5 = 3^5 - 3 \\cdot 2^5 + 3 \\cdot 1^5 = 243 - 96 + 3 = 150$.',
        hindi: 'सूत्र $\\sum (-1)^r \\binom{3}{r}(3-r)^5 = 3^5 - 3 \\cdot 2^5 + 3 \\cdot 1^5 = 243 - 96 + 3 = 150$ का उपयोग करें।',
      },
    },
  ],
  next: {
    slug: 'straight-lines',
    title: 'Straight Lines',
  },
};

export default chapter;
